import React, { useEffect, useState } from "react";
import { UserPlus, UserCheck, UserX, X as XIcon, Loader2, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useMessages } from "@/contexts/MessagesContext";

/**
 * Renders Add Friend / Cancel / Accept-Reject / Remove buttons + Message
 * shortcut on a profile page. Pure stateless wrapper around /api/friends.
 */
export default function FriendButton({ userId, peerName, peerPicture }) {
  const { user } = useAuth();
  const { openWith } = useMessages();
  const [status, setStatus] = useState(null);   // 'self'|'none'|'friends'|'pending_in'|'pending_out'
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user || !userId || userId === user.user_id) {
      setStatus(userId === user?.user_id ? "self" : null);
      return;
    }
    try {
      const { data } = await api.get(`/friends/status/${userId}`);
      setStatus(data?.status || "none");
    } catch {
      setStatus("none");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, user?.user_id]);

  if (!user) return null;
  if (status === "self" || userId === user.user_id) return null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const sendRequest = () => run(async () => {
    const { data } = await api.post(`/friends/request/${userId}`);
    setStatus(data.status);
    toast.success(data.status === "friends" ? "You are now friends" : "Request sent");
  });
  const accept = () => run(async () => {
    const { data } = await api.post(`/friends/accept/${userId}`);
    setStatus(data.status);
    toast.success("Friend request accepted");
  });
  const reject = () => run(async () => {
    await api.post(`/friends/reject/${userId}`);
    setStatus("none");
    toast.success("Request rejected");
  });
  const cancelOrRemove = () => run(async () => {
    await api.delete(`/friends/${userId}`);
    setStatus("none");
    toast.success(status === "friends" ? "Friend removed" : "Request cancelled");
  });

  const openDm = () => openWith({ user_id: userId, name: peerName, picture: peerPicture });

  // Render
  let actions;
  if (status === "none") {
    actions = (
      <button
        onClick={sendRequest}
        disabled={busy}
        data-testid="friend-add-btn"
        className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
        Add Friend
      </button>
    );
  } else if (status === "pending_out") {
    actions = (
      <button
        onClick={cancelOrRemove}
        disabled={busy}
        data-testid="friend-cancel-btn"
        className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full border border-[var(--tt-border)] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XIcon className="w-3.5 h-3.5" />}
        Cancel Request
      </button>
    );
  } else if (status === "pending_in") {
    actions = (
      <div className="flex items-center gap-2">
        <button
          onClick={accept}
          disabled={busy}
          data-testid="friend-accept-btn"
          className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
          Accept
        </button>
        <button
          onClick={reject}
          disabled={busy}
          data-testid="friend-reject-btn"
          className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full border border-[var(--tt-border)] disabled:opacity-50"
        >
          <UserX className="w-3.5 h-3.5" />
          Reject
        </button>
      </div>
    );
  } else if (status === "friends") {
    actions = (
      <button
        onClick={cancelOrRemove}
        disabled={busy}
        data-testid="friend-remove-btn"
        className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-red-500/15 text-white hover:text-red-300 text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full border border-[var(--tt-border)] hover:border-red-500/40 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5 text-[var(--tt-primary)]" />}
        Friends
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {actions}
      <button
        onClick={openDm}
        data-testid="friend-message-btn"
        className="inline-flex items-center gap-1.5 bg-[var(--tt-surface-2)] hover:bg-[#222] text-white text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-full border border-[var(--tt-border)] hover:border-[var(--tt-primary)]"
      >
        <MessageCircle className="w-3.5 h-3.5 text-[var(--tt-purple)]" />
        Message
      </button>
    </div>
  );
}
